import { useState, useEffect, useRef } from 'react'
import {
  Home,
  DollarSign,
  MapPin,
  CheckSquare,
  Building2,
  Landmark,
  FileCheck,
  CreditCard,
  Send,
  Lock,
  Layers,
  MessageSquare,
  BanknoteIcon,
  Info,
  FileSignature,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Textarea } from '../components/ui/Textarea'
import { Label } from '../components/ui/Label'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '../components/ui/Card'
import { Logo } from '../components/ui/Logo'
import { HubTemplate } from '../components/wizard/HubTemplate'
import { RequestEditModal } from '../components/wizard/HubShell'
import {
  FileUploadZone,
  TwoMethodToggle,
  OptionCardGrid,
  ConditionalInfoBox,
  MultiRowFormSection,
} from '../components/wizard/HubPrimitives'
import { DocumentChecklist } from '../components/wizard/DocumentChecklist'
import { DocumentSigningModal } from '../components/wizard/DocumentSigningModal'
import type { HubTemplateStep } from '../components/wizard/HubTemplate'
import type { HubColor } from '../components/wizard/HubShell'
import type { HubDocument } from '../config/hubDocuments'
import type { DocumentStatus } from '../components/wizard/DocumentChecklist'

const NAV_SECTIONS = [
  { id: 'buttons', label: 'Buttons' },
  { id: 'form-primitives', label: 'Form primitives' },
  { id: 'cards', label: 'Cards' },
  { id: 'dialogs-modals', label: 'Dialogs / Modals' },
  { id: 'hub-template', label: 'Hub Template' },
  { id: 'hub-ui-patterns', label: 'Hub UI Patterns' },
]

const NAV_SECTION_IDS = NAV_SECTIONS.map((s) => s.id)

function useActiveSection(sectionIds: string[]) {
  const [activeId, setActiveId] = useState(sectionIds[0])
  useEffect(() => {
    const observers: IntersectionObserver[] = []
    sectionIds.forEach((id) => {
      const element = document.getElementById(id)
      if (!element) return
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveId(id)
        },
        { rootMargin: '-20% 0px -70% 0px' }
      )
      observer.observe(element)
      observers.push(observer)
    })
    return () => observers.forEach((o) => o.disconnect())
  }, [sectionIds])
  return activeId
}


interface DemoConfig {
  label: string
  description: string
  buttonLabel: string
  icon: LucideIcon
  hubIcon: LucideIcon
  color: HubColor
  title: string
  subtitle: string
  submitted: boolean
  prerequisitesMet: boolean
  prerequisiteMessage?: string
  prerequisiteHubName?: string
  completionPercent: number
  activeStepId: string
  lastSavedAt?: string | null
  showSaveIndicator: boolean
  withRequestEdit: boolean
  tickingAutoSave?: boolean
}

const demoConfigs: DemoConfig[] = [
  {
    label: 'Green variant',
    description: 'The green color theme — used for Sale/Escrow hubs.',
    buttonLabel: 'Open Green Hub',
    icon: Building2,
    hubIcon: Building2,
    color: 'green',
    title: 'Sale Hub',
    subtitle: 'Capture the details of your relinquished property sale.',
    submitted: false,
    prerequisitesMet: true,
    completionPercent: 50,
    activeStepId: 'overview',
    showSaveIndicator: true,
    withRequestEdit: false,
  },
  {
    label: 'Blue variant',
    description: 'The blue color theme — used for Exchange/Identification hubs.',
    buttonLabel: 'Open Blue Hub',
    icon: FileCheck,
    hubIcon: FileCheck,
    color: 'blue',
    title: 'Identification Hub',
    subtitle: 'Identify replacement properties within 45 days.',
    submitted: false,
    prerequisitesMet: true,
    completionPercent: 25,
    activeStepId: 'details',
    showSaveIndicator: true,
    withRequestEdit: false,
  },
  {
    label: 'Purple variant',
    description: 'The purple color theme — used for Purchase hubs.',
    buttonLabel: 'Open Purple Hub',
    icon: Landmark,
    hubIcon: Landmark,
    color: 'purple',
    title: 'Purchase Hub',
    subtitle: 'Record the acquisition of your replacement property.',
    submitted: false,
    prerequisitesMet: true,
    completionPercent: 75,
    activeStepId: 'location',
    showSaveIndicator: true,
    withRequestEdit: false,
  },
  {
    label: 'Orange variant',
    description: 'The orange color theme — used for Disbursement hubs.',
    buttonLabel: 'Open Orange Hub',
    icon: CreditCard,
    hubIcon: CreditCard,
    color: 'orange',
    title: 'Disbursement Hub',
    subtitle: 'Authorize and confirm fund release from escrow.',
    submitted: false,
    prerequisitesMet: true,
    completionPercent: 0,
    activeStepId: 'overview',
    showSaveIndicator: true,
    withRequestEdit: false,
  },
  {
    label: 'Cyan variant',
    description: 'The cyan color theme — available for future hub types.',
    buttonLabel: 'Open Cyan Hub',
    icon: Send,
    hubIcon: Send,
    color: 'cyan',
    title: 'Transfer Hub',
    subtitle: 'Review and approve the final fund transfer.',
    submitted: false,
    prerequisitesMet: true,
    completionPercent: 33,
    activeStepId: 'overview',
    showSaveIndicator: true,
    withRequestEdit: false,
  },
  {
    label: 'Submitted + Request Edit',
    description: 'A hub in the submitted state — shows RequestEditButton in footer.',
    buttonLabel: 'Open Submitted Hub',
    icon: CheckSquare,
    hubIcon: FileCheck,
    color: 'blue',
    title: 'Identification Hub',
    subtitle: 'Identify replacement properties within 45 days.',
    submitted: true,
    prerequisitesMet: true,
    completionPercent: 100,
    activeStepId: 'overview',
    showSaveIndicator: false,
    withRequestEdit: true,
  },
  {
    label: 'Locked (prerequisites not met)',
    description: 'A hub gated by an incomplete prerequisite — shows a small dialog instead of the full hub modal.',
    buttonLabel: 'Open Locked Hub',
    icon: Lock,
    hubIcon: Landmark,
    color: 'purple',
    title: 'Purchase Hub',
    subtitle: 'Record the acquisition of your replacement property.',
    submitted: false,
    prerequisitesMet: false,
    prerequisiteMessage: 'Complete the Identification Hub first to unlock this section.',
    prerequisiteHubName: 'Identification Hub',
    completionPercent: 0,
    activeStepId: 'overview',
    showSaveIndicator: false,
    withRequestEdit: false,
  },
  {
    label: 'Partial completion + ticking auto-save',
    description: '2 of 4 steps complete; auto-save indicator ticks every 10 seconds while the hub is open.',
    buttonLabel: 'Open Partial Hub',
    icon: Layers,
    hubIcon: Building2,
    color: 'green',
    title: 'Sale Hub',
    subtitle: 'Capture the details of your relinquished property sale.',
    submitted: false,
    prerequisitesMet: true,
    completionPercent: 50,
    activeStepId: 'location',
    lastSavedAt: null,
    showSaveIndicator: true,
    withRequestEdit: false,
    tickingAutoSave: true,
  },
]

interface ActiveDemo {
  configIndex: number
  activeStepId: string
  confirmStates: Record<string, boolean>
  lastSavedAt: string | null
}

const STEP_DEFINITIONS = [
  {
    id: 'overview',
    label: 'Overview',
    icon: Home,
    confirmLabel: 'I confirm these answers are complete.',
    intro: {
      title: 'Start here',
      body: 'Review the overview of this hub and confirm you understand what is required.',
    },
    content: (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          This is a mock overview step. In a real hub, you would see key information about the exchange here.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg border border-border p-3 bg-muted/30">
            <p className="text-xs text-muted-foreground">Exchange ID</p>
            <p className="text-sm font-semibold text-foreground">EX-DEMO-001</p>
          </div>
          <div className="rounded-lg border border-border p-3 bg-muted/30">
            <p className="text-xs text-muted-foreground">Status</p>
            <p className="text-sm font-semibold text-foreground">In Progress</p>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 'details',
    label: 'Details',
    icon: DollarSign,
    confirmLabel: 'I confirm the exchanger information is accurate.',
    intro: {
      body: 'Fill in the financial details for this exchange. All figures are required.',
    },
    content: (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Mock details step. Real forms use React Hook Form + Zod here.
        </p>
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="block text-xs font-medium text-foreground">Property Value</label>
            <div className="w-full px-3 py-2 rounded-md border border-border bg-muted text-sm text-muted-foreground">
              Placeholder input
            </div>
          </div>
          <div className="space-y-1">
            <label className="block text-xs font-medium text-foreground">Mortgage Balance</label>
            <div className="w-full px-3 py-2 rounded-md border border-border bg-muted text-sm text-muted-foreground">
              Placeholder input
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 'location',
    label: 'Location',
    icon: MapPin,
    confirmLabel: 'I confirm the property and buyer details are accurate.',
    intro: {
      title: 'Property location',
      body: 'Enter the address of the property involved in this exchange.',
    },
    content: (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">Mock location step.</p>
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="block text-xs font-medium text-foreground">Street Address</label>
            <div className="w-full px-3 py-2 rounded-md border border-border bg-muted text-sm text-muted-foreground">
              Placeholder input
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="block text-xs font-medium text-foreground">City</label>
              <div className="w-full px-3 py-2 rounded-md border border-border bg-muted text-sm text-muted-foreground">
                Placeholder
              </div>
            </div>
            <div className="space-y-1">
              <label className="block text-xs font-medium text-foreground">State</label>
              <div className="w-full px-3 py-2 rounded-md border border-border bg-muted text-sm text-muted-foreground">
                Placeholder
              </div>
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 'confirm',
    label: 'Confirm',
    icon: CheckSquare,
    confirmLabel: 'I confirm all information above is accurate and complete.',
    intro: {
      title: 'Final confirmation',
      body: 'Review all information and confirm everything is accurate before submitting.',
    },
    content: (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">Review your entries and confirm accuracy.</p>
        <div className="rounded-lg border border-border p-4 bg-muted/30 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Exchange ID</span>
            <span className="font-medium text-foreground">EX-DEMO-001</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Property Value</span>
            <span className="font-medium text-foreground">—</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Location</span>
            <span className="font-medium text-foreground">—</span>
          </div>
        </div>
      </div>
    ),
  },
]

function buildSteps(
  confirmStates: Record<string, boolean>,
  onConfirmChange: (id: string, checked: boolean) => void
): HubTemplateStep[] {
  return STEP_DEFINITIONS.map((def, index) => ({
    id: def.id,
    label: def.label,
    icon: def.icon,
    fieldsComplete: index < 2,
    confirmation: {
      label: def.confirmLabel,
      checked: confirmStates[def.id] ?? false,
      onChange: (checked: boolean) => onConfirmChange(def.id, checked),
    },
    intro: def.intro,
    content: def.content,
  }))
}

const DEMO_DOCUMENTS: HubDocument[] = [
  {
    name: 'Exchange Agreement',
    required: true,
    actionType: 'e-sign',
    description: 'QI exchange agreement between exchanger and qualified intermediary',
    category: 'documents',
  },
  {
    name: 'W-9 Tax ID Certification',
    required: true,
    actionType: 'e-sign',
    description: 'IRS Form W-9 for tax identification verification',
    category: 'irs',
  },
  {
    name: 'Closing Statement',
    required: false,
    actionType: 'upload',
    description: 'Settlement statement from the closing company',
    category: 'documents',
  },
  {
    name: 'Title Insurance',
    required: false,
    actionType: 'upload',
    description: 'Title insurance commitment or policy',
    category: 'property',
  },
]

const DEMO_DOCUMENT_STATUSES: Record<string, DocumentStatus> = {
  'Exchange Agreement': { uploaded: false, signed: true },
  'W-9 Tax ID Certification': { uploaded: true, signed: false },
  'Closing Statement': { uploaded: false, signed: false },
  'Title Insurance': { uploaded: false, signed: false },
}

const DEMO_EXCHANGER_DATA = {
  names: 'Demo Exchanger',
  homeAddress: 'Redacted for demo',
  taxIdNumber: 'XXXX',
}

export function StyleGuidePage() {
  const activeSection = useActiveSection(NAV_SECTION_IDS)
  const [activeDemo, setActiveDemo] = useState<ActiveDemo | null>(null)
  const [requestEditOpen, setRequestEditOpen] = useState(false)
  const tickIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const [uploadState, setUploadState] = useState<'idle' | 'verifying' | 'success' | 'error'>('idle')
  const [uploadFilename, setUploadFilename] = useState<string | undefined>(undefined)
  const [toggleValue, setToggleValue] = useState('wire')
  const [optionValue, setOptionValue] = useState('3-property')
  const [signingModalOpen, setSigningModalOpen] = useState(false)
  const [multiRows, setMultiRows] = useState([
    { id: 'exchanger-1', content: <p className="text-sm text-muted-foreground">Exchanger 1 fields would render here.</p> },
    { id: 'exchanger-2', content: <p className="text-sm text-muted-foreground">Exchanger 2 fields would render here.</p> },
  ])

  useEffect(() => {
    if (activeDemo?.configIndex !== undefined) {
      const config = demoConfigs[activeDemo.configIndex]
      if (config?.tickingAutoSave) {
        tickIntervalRef.current = setInterval(() => {
          setActiveDemo((prev) =>
            prev ? { ...prev, lastSavedAt: new Date().toISOString() } : prev
          )
        }, 10000)
        setActiveDemo((prev) =>
          prev ? { ...prev, lastSavedAt: new Date().toISOString() } : prev
        )
      }
    }
    return () => {
      if (tickIntervalRef.current) {
        clearInterval(tickIntervalRef.current)
        tickIntervalRef.current = null
      }
    }
  }, [activeDemo?.configIndex])

  function openDemo(index: number) {
    setActiveDemo({
      configIndex: index,
      activeStepId: demoConfigs[index].activeStepId,
      confirmStates: {},
      lastSavedAt: demoConfigs[index].lastSavedAt ?? null,
    })
  }

  function closeDemo() {
    setActiveDemo(null)
  }

  function handleConfirmChange(stepId: string, checked: boolean) {
    setActiveDemo((prev) =>
      prev
        ? { ...prev, confirmStates: { ...prev.confirmStates, [stepId]: checked } }
        : prev
    )
  }

  const activeConfig = activeDemo !== null ? demoConfigs[activeDemo.configIndex] : null

  function handleDemoFileSelect(file: File) {
    setUploadFilename(file.name)
    setUploadState('verifying')
    setTimeout(() => setUploadState('success'), 1500)
  }

  function handleDemoFileRemove() {
    setUploadFilename(undefined)
    setUploadState('idle')
  }

  function addMultiRow() {
    const nextId = `exchanger-${multiRows.length + 1}`
    setMultiRows((prev) => [
      ...prev,
      {
        id: nextId,
        content: (
          <p className="text-sm text-muted-foreground">
            Exchanger {prev.length + 1} fields would render here.
          </p>
        ),
      },
    ])
  }

  function removeMultiRow(id: string) {
    setMultiRows((prev) => prev.filter((r) => r.id !== id))
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-background/95 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-4">
          <Logo
            className="h-8 w-auto"
            alt="Open1031"
            forceDefault
          />
          <div className="h-5 w-px bg-border" />
          <span className="text-sm font-medium text-muted-foreground">Style Guide</span>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground tracking-tight">Open1031 Style Guide</h1>
          <p className="mt-2 text-base text-muted-foreground leading-relaxed max-w-2xl">
            This page is the rendered companion to{' '}
            <code className="text-sm font-mono bg-muted px-1 py-0.5 rounded">docs/STYLE_GUIDE.md</code>
            {' '}— see that document for brand voice, color tokens, and component conventions.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-8 items-start">
          <nav
            className="hidden lg:flex flex-col gap-1 sticky top-[73px]"
            aria-label="Style guide sections"
          >
            {NAV_SECTIONS.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className={
                  activeSection === section.id
                    ? 'text-sm font-medium text-primary px-3 py-1.5 rounded-md bg-primary/10'
                    : 'text-sm text-muted-foreground px-3 py-1.5 rounded-md hover:text-foreground hover:bg-accent transition-colors'
                }
              >
                {section.label}
              </a>
            ))}
          </nav>

          <div className="space-y-14">
            <section id="buttons" aria-labelledby="buttons-heading">
              <h2
                id="buttons-heading"
                className="text-xl font-semibold text-foreground mb-4 tracking-tight"
              >
                Buttons
              </h2>
              <Card>
                <CardContent className="pt-6 space-y-6">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-3">
                      Variants
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <Button variant="default">Primary</Button>
                      <Button variant="secondary">Secondary</Button>
                      <Button variant="outline">Outline</Button>
                      <Button variant="ghost">Ghost</Button>
                      <Button variant="destructive">Destructive</Button>
                      <Button variant="link">Link</Button>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-3">
                      Sizes
                    </p>
                    <div className="flex flex-wrap items-center gap-3">
                      <Button size="sm">Small</Button>
                      <Button size="default">Default</Button>
                      <Button size="lg">Large</Button>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-3">
                      States
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <Button disabled>Disabled</Button>
                      <Button variant="outline" disabled>Outline disabled</Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>

            <section id="form-primitives" aria-labelledby="form-heading">
              <h2
                id="form-heading"
                className="text-xl font-semibold text-foreground mb-4 tracking-tight"
              >
                Form primitives
              </h2>
              <Card>
                <CardContent className="pt-6 space-y-5">
                  <div className="space-y-1.5">
                    <Label htmlFor="demo-input">Text input</Label>
                    <Input
                      id="demo-input"
                      placeholder="Placeholder text"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="demo-input-error">Input — error state</Label>
                    <Input
                      id="demo-input-error"
                      placeholder="Invalid value"
                      className="border-destructive focus-visible:ring-destructive"
                    />
                    <p className="text-xs text-destructive">This field is required.</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="demo-input-disabled">Input — disabled</Label>
                    <Input
                      id="demo-input-disabled"
                      placeholder="Disabled"
                      disabled
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="demo-textarea">Textarea</Label>
                    <Textarea
                      id="demo-textarea"
                      placeholder="Enter your message..."
                      rows={4}
                    />
                  </div>
                </CardContent>
              </Card>
            </section>

            <section id="cards" aria-labelledby="cards-heading">
              <h2
                id="cards-heading"
                className="text-xl font-semibold text-foreground mb-4 tracking-tight"
              >
                Cards
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Card title</CardTitle>
                    <CardDescription>
                      Card description — secondary information about the card contents.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">Card body content goes here.</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">With footer</CardTitle>
                    <CardDescription>
                      Cards can include a footer with actions.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">Card body content.</p>
                  </CardContent>
                  <div className="flex items-center px-6 pb-6 pt-0 gap-2">
                    <Button size="sm">Primary</Button>
                    <Button size="sm" variant="outline">Secondary</Button>
                  </div>
                </Card>
              </div>
            </section>

            <section id="dialogs-modals" aria-labelledby="dialogs-heading">
              <h2
                id="dialogs-heading"
                className="text-xl font-semibold text-foreground mb-4 tracking-tight"
              >
                Dialogs / Modals
              </h2>
              <div className="space-y-6">
                <p className="text-sm text-muted-foreground">
                  Dialogs use the shadcn{' '}
                  <code className="text-xs font-mono bg-muted px-1 py-0.5 rounded">Dialog</code>
                  {' '}primitive (Radix UI). They portal to{' '}
                  <code className="text-xs font-mono bg-muted px-1 py-0.5 rounded">document.body</code>,
                  trap focus, and close on Escape.
                </p>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base font-semibold">
                      Request Edit dialog (preview)
                    </CardTitle>
                    <CardDescription>
                      What the user sees when they click Request Edit on a submitted hub. The dialog portals
                      to document.body in production — click the button below to see the live version.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                          <MessageSquare className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-foreground">Request Edit</p>
                          <p className="text-xs text-muted-foreground">Submitted section</p>
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="request-edit-preview-reason">What changes do you need?</Label>
                        <Textarea
                          id="request-edit-preview-reason"
                          placeholder="Please describe the edits or corrections needed..."
                          rows={4}
                          readOnly
                        />
                      </div>
                      <div className="bg-muted/60 border border-border rounded-lg p-3 text-xs text-muted-foreground">
                        Our support team will review your request and contact you within 1-2 business days.
                      </div>
                      <div className="flex justify-between">
                        <Button variant="outline" size="sm" disabled>Cancel</Button>
                        <Button size="sm" disabled>Submit Request</Button>
                      </div>
                    </div>
                    <div className="mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setRequestEditOpen(true)}
                      >
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Open live Request Edit dialog
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </section>

            <section id="hub-template" aria-labelledby="hub-template-heading">
              <h2
                id="hub-template-heading"
                className="text-xl font-semibold text-foreground mb-4 tracking-tight"
              >
                Hub Template
              </h2>
              <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                The{' '}
                <code className="text-xs font-mono bg-muted px-1 py-0.5 rounded">HubTemplate</code>
                {' '}composite component wires together HubShell, StepTabs, StepIntroNote,
                ConfirmationToggle, PrerequisiteDialog, and RequestEditButton. Every step now
                requires{' '}
                <code className="text-xs font-mono bg-muted px-1 py-0.5 rounded">fieldsComplete</code>
                {' '}and a{' '}
                <code className="text-xs font-mono bg-muted px-1 py-0.5 rounded">confirmation</code>
                {' '}object. Click any card below to open a live demo.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {demoConfigs.map((config, index) => {
                  const CardIcon = config.icon
                  return (
                    <div
                      key={config.label}
                      className="rounded-xl border border-border bg-card/80 p-4 flex flex-col gap-3"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 p-2 rounded-lg bg-muted">
                          <CardIcon
                            className="h-4 w-4 text-muted-foreground"
                            aria-hidden="true"
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-foreground">{config.label}</p>
                          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                            {config.description}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openDemo(index)}
                        className="self-start"
                      >
                        {config.buttonLabel}
                      </Button>
                    </div>
                  )
                })}
              </div>
            </section>

            <section id="hub-ui-patterns" aria-labelledby="hub-ui-patterns-heading">
              <h2
                id="hub-ui-patterns-heading"
                className="text-xl font-semibold text-foreground mb-4 tracking-tight"
              >
                Hub UI Patterns
              </h2>
              <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                Reusable primitives shared across all hub types. Each accepts a{' '}
                <code className="text-xs font-mono bg-muted px-1 py-0.5 rounded">color</code>
                {' '}prop and uses static lookup maps — no dynamic Tailwind interpolation.
              </p>

              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base font-semibold">FileUploadZone</CardTitle>
                    <CardDescription>
                      Dashed drop area with four states: idle, verifying, success, error. Click the
                      idle zone below to simulate a file pick; it will transition through verifying →
                      success automatically.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-2">
                          Live demo (green)
                        </p>
                        <FileUploadZone
                          label="Upload Bank Statement"
                          hint="PDF, JPG, or PNG accepted"
                          state={uploadState}
                          filename={uploadFilename}
                          color="green"
                          onSelect={handleDemoFileSelect}
                          onRemove={handleDemoFileRemove}
                          summary={
                            uploadState === 'success' ? (
                              <p className="text-xs text-green-700 dark:text-green-300">
                                Extracted balance: $125,000
                              </p>
                            ) : null
                          }
                        />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-2">
                          Error state (cyan)
                        </p>
                        <FileUploadZone
                          label="Upload Document"
                          state="error"
                          color="cyan"
                          onSelect={() => {}}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base font-semibold">TwoMethodToggle</CardTitle>
                    <CardDescription>
                      Segmented button pair for binary method selection. Selected option gets a
                      solid colored background; unselected stays muted.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">
                        Orange variant
                      </p>
                      <TwoMethodToggle
                        value={toggleValue}
                        options={[
                          { id: 'wire', label: 'Wire Transfer', icon: BanknoteIcon },
                          { id: 'check', label: 'Check', icon: FileCheck },
                        ]}
                        onChange={setToggleValue}
                        color="orange"
                      />
                      <p className="text-xs text-muted-foreground">
                        Selected: <strong>{toggleValue}</strong>
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base font-semibold">OptionCardGrid</CardTitle>
                    <CardDescription>
                      Full-width radio option cards. Selected card gets a colored border and tinted
                      background. Three options, one selected.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="max-w-sm space-y-3">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">
                        Blue variant — identification rule
                      </p>
                      <OptionCardGrid
                        value={optionValue}
                        options={[
                          {
                            id: '3-property',
                            label: '3-Property Rule',
                            description: 'Identify up to 3 replacement properties.',
                          },
                          {
                            id: '200-percent',
                            label: '200% Rule',
                            description: 'Aggregate FMV cannot exceed 200% of relinquished property.',
                          },
                          {
                            id: '95-percent',
                            label: '95% Rule',
                            description: 'Must acquire at least 95% of identified property value.',
                          },
                        ]}
                        onChange={setOptionValue}
                        color="blue"
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base font-semibold">ConditionalInfoBox</CardTitle>
                    <CardDescription>
                      Bordered callout with icon, bold title, and explainer text. Tinted background
                      matches the hub color.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <ConditionalInfoBox
                      icon={Info}
                      title="Why does this matter?"
                      color="blue"
                    >
                      The 45-day identification deadline is a strict legal threshold under Treas.
                      Reg. §1.1031(k)-1(c)(1). Missing it disqualifies the entire exchange.
                    </ConditionalInfoBox>
                    <ConditionalInfoBox
                      icon={Info}
                      title="Constructive receipt risk"
                      color="orange"
                    >
                      If you receive exchange funds directly, constructive receipt is triggered and
                      the exchange is immediately disqualified. All proceeds must flow through the
                      Qualified Intermediary.
                    </ConditionalInfoBox>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base font-semibold">MultiRowFormSection</CardTitle>
                    <CardDescription>
                      Card wrapper for repeatable form rows. Caller passes a ReactNode per row.
                      Add / remove buttons are optional.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <MultiRowFormSection
                      title="Exchangers"
                      rows={multiRows}
                      onAdd={addMultiRow}
                      onRemove={removeMultiRow}
                      addLabel="Add Exchanger"
                      color="purple"
                    />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base font-semibold">DocumentChecklist</CardTitle>
                    <CardDescription>
                      Document manifest showing required, optional, signed, and uploaded states.
                      Uses a static color map keyed on hub color.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <DocumentChecklist
                      documents={DEMO_DOCUMENTS}
                      statuses={DEMO_DOCUMENT_STATUSES}
                      hubColor="green"
                    />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base font-semibold">DocumentSigningModal</CardTitle>
                    <CardDescription>
                      Full-screen signing modal with document preview and type/draw signature
                      capture. Opens in a portal on{' '}
                      <code className="text-xs font-mono bg-muted px-1 py-0.5 rounded">
                        document.body
                      </code>
                      .
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSigningModalOpen(true)}
                    >
                      <FileSignature className="h-4 w-4 mr-2" />
                      Open DocumentSigningModal
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </section>
          </div>
        </div>
      </div>

      {signingModalOpen && (
        <DocumentSigningModal
          documentName="Exchange Agreement"
          documentType="exchange-agreement"
          exchangerData={DEMO_EXCHANGER_DATA}
          onSign={() => setSigningModalOpen(false)}
          onClose={() => setSigningModalOpen(false)}
        />
      )}

      <RequestEditModal
        open={requestEditOpen}
        color="blue"
        onSubmit={() => setRequestEditOpen(false)}
        onClose={() => setRequestEditOpen(false)}
      />

      {activeDemo !== null && activeConfig !== null && (
        <HubTemplate
          icon={activeConfig.hubIcon}
          color={activeConfig.color}
          title={activeConfig.title}
          subtitle={activeConfig.subtitle}
          onClose={closeDemo}
          submitted={activeConfig.submitted}
          completionPercent={activeConfig.completionPercent}
          prerequisitesMet={activeConfig.prerequisitesMet}
          prerequisiteMessage={activeConfig.prerequisiteMessage}
          prerequisiteHubName={activeConfig.prerequisiteHubName}
          steps={buildSteps(activeDemo.confirmStates, handleConfirmChange)}
          activeStepId={activeDemo.activeStepId}
          onStepChange={(id) =>
            setActiveDemo((prev) => prev ? { ...prev, activeStepId: id } : prev)
          }
          sectionLabel="Quick Steps"
          onSubmit={closeDemo}
          submitLabel="Submit Hub"
          submitIcon={Send}
          completedMessage={`${activeConfig.title} Completed`}
          lastSavedAt={activeDemo.lastSavedAt ?? activeConfig.lastSavedAt}
          showSaveIndicator={activeConfig.showSaveIndicator}
          onRequestEdit={activeConfig.withRequestEdit ? () => {} : undefined}
        />
      )}
    </div>
  )
}
